import React, { useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, IconButton, Icon, Slide, MenuItem, Select, Button } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { BRAND, MODAL_WIDTH_SM, FONT_SIZE_PX } from "../theme/uiTokens";

// Aapka DateRangePicker
import DateRangePicker from "layouts/DateTimerange/dateTimeRange"; 

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function PlaybackHistoryModal({ open, onClose, vehicleId, imei, sessionToken }) {
  
  // 👇 Default dates ke liye helper function
  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayStr = getTodayDateString();
  const defaultStart = `${todayStr}T00:00`; // 👈 Start Time: 00:00
  const defaultEnd = `${todayStr}T11:59`;   // 👈 End Time: 11:59

  // --- Form States ---
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);
  const [channel, setChannel] = useState(1);

  // --- API/Video States ---
  const [streamUrl, setStreamUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Helper function for DateTime conversion
  function toIsoZ(localDateTimeValue) {
    // datetime-local gives "YYYY-MM-DDTHH:mm" with no timezone; treat as UTC
    return `${localDateTimeValue}:00Z`;
  }

 const handleLoadStream = () => {
    // 1. Console logs tak ki aap console mein dekh sakein ki picker kya return kar raha hai
    console.log("Raw Start Time from Picker:", startTime);
    console.log("Raw End Time from Picker:", endTime);

    if (!imei || imei === "N/A") {
      setErrorMsg("Error: IMEI/SIM number missing for this vehicle.");
      return;
    }
    if (!startTime || !endTime) {
      setErrorMsg("Error: Both start and end dates are required.");
      return;
    }

    // 2. String/Object ko Date object mein convert karein
    const startDateObj = new Date(startTime);
    const endDateObj = new Date(endTime);

    console.log("Parsed Start Date Object:", startDateObj);
    console.log("Parsed End Date Object:", endDateObj);

    // 3. Check karein ki DateRangePicker ne koi invalid date format toh nahi bheja
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
      setErrorMsg("Error: Invalid date format received. Check console for details.");
      return;
    }

    // 4. Time Comparison (>= matlab start time hamesha end time se chhota hona chahiye)
    if (startDateObj >= endDateObj) {
      setErrorMsg("Error: Start date must be before end date.");
      return;
    }

    // Agar sab sahi hai toh error clear karein
    setErrorMsg("");
    setStreamUrl(null); // Reset to trigger reload

    // URL parameters builder
    const params = new URLSearchParams({
      sim: imei.trim(),
      channel: String(channel).trim() || '1',
      from: toIsoZ(startTime), // Note: Ensure toIsoZ can handle what DateRangePicker returns
      to: toIsoZ(endTime),
      timeout: '120',
    });

    // Set the stream URL for the video tag
    setStreamUrl(`https://camera1.dhundhoo.com/internal/recordings/stream?${params.toString()}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      keepMounted
      hideBackdrop={true} 
      disableEnforceFocus
      style={{ pointerEvents: "none" }} 
      sx={{
        "& .MuiDialog-container": {
          justifyContent: "flex-end", 
          alignItems: "flex-start",   
        }
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto", 
          m: 2,
          mt: 10, 
          borderRadius: "8px",
          width: MODAL_WIDTH_SM,
          boxShadow: 10
        }
      }}
    >
      {/* --- HEADER --- */}
      <MDBox 
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          backgroundColor: BRAND.accent,
          color: "#fff"
        }}
      >
        <MDTypography variant="h6" color="white" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Icon>replay</Icon>
          Playback Details
        </MDTypography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <Icon>close</Icon>
        </IconButton>
      </MDBox>

      {/* --- BODY --- */}
      <DialogContent dividers>
        
        {/* FILTERS */}
        <MDBox display="flex" flexDirection="column" gap={2} mb={2}>
          <MDBox display="flex" gap={2} alignItems="center">
            <MDTypography variant="caption" fontWeight="bold">Channel:</MDTypography>
            <Select 
              value={channel} 
              onChange={(e) => setChannel(e.target.value)}
              size="small" 
              sx={{ height: 36, width: 100 }}
            >
              <MenuItem value={1}>CH1</MenuItem>
              <MenuItem value={2}>CH2</MenuItem>
              <MenuItem value={3}>CH3</MenuItem>
              <MenuItem value={4}>CH4</MenuItem>
            </Select>
          </MDBox>

          <MDBox width="100%">
             <DateRangePicker
               startTime={startTime}
               endTime={endTime}
               onChange={({ startTime: s, endTime: e }) => {
                 setStartTime(s);
                 setEndTime(e);
               }}
             />
          </MDBox>

          <Button 
            variant="contained" 
            onClick={handleLoadStream}
            sx={{ 
              height: 36, 
              width: "100%", 
              mt: 1, 
              backgroundColor: BRAND.accent, 
              color: "#fff", 
              "&:hover": { backgroundColor: BRAND.accentHover } 
            }}
          >
            Load Stream
          </Button>
        </MDBox>

        {/* VIDEO PLAYER VIEWER */}
        <MDBox 
          sx={{ 
            backgroundColor: "#222", 
            p: 2, 
            borderRadius: 2, 
            minHeight: "250px", 
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            color: "#fff"
          }}
        >
          {errorMsg ? (
            <MDTypography color="error" variant="body2" sx={{ textAlign: "center" }}>
              {errorMsg}
            </MDTypography>
          ) : streamUrl ? (
            <video
              key={streamUrl}
              controls
              autoPlay
              muted
              style={{
                width: "100%",
                maxHeight: "350px",
                background: "#000",
                borderRadius: "6px",
                border: "1px solid #1D2126",
              }}
              src={streamUrl}
              onError={() => setErrorMsg("Stream failed to load. Check IMEI, channel, and date range.")}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <MDTypography variant="body2" sx={{ opacity: 0.6, textAlign: "center" }}>
              Select "From" & "To" date-time and click "Load Stream" to view the playback.
            </MDTypography>
          )}
        </MDBox>

      </DialogContent>
    </Dialog>
  );
}

PlaybackHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleId: PropTypes.string,
  imei: PropTypes.string,
  sessionToken: PropTypes.string
};

export default PlaybackHistoryModal;